import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';

import { api, type ActivityPage, type AdminOverview } from '../../lib/api';
import { ActivityTable } from './ActivityTable';

/**
 * The cross-tenant activity feed.
 *
 * Paginated by keyset on the audit id, matching the API — the trail only grows, and an
 * OFFSET scan over it gets slower every day the product is alive.
 *
 * Filters live in the URL so a link to "restricted-record reads at this carrier" can be
 * pasted into a ticket and still mean the same thing tomorrow.
 */

/** Coarse groupings; the API matches on an action prefix. */
const ACTION_FILTERS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Everything' },
  { value: 'auth.', label: 'Sign-ins and sessions' },
  { value: 'auth.impersonation.', label: 'Milepost workspace access' },
  { value: 'document.', label: 'Documents' },
  { value: 'document.restricted_access', label: 'Restricted record access' },
  { value: 'driver.', label: 'Driver records' },
  { value: 'alert.', label: 'Alerts' },
  { value: 'admin.', label: 'Console access' },
];

const PAGE_SIZE = 100;

export function Activity() {
  const [params, setParams] = useSearchParams();
  const tenantId = params.get('tenantId') ?? '';
  const action = params.get('action') ?? '';

  // Only for the carrier picker; the feed itself does not need the rollup.
  const { data: overview } = useQuery({
    queryKey: ['admin', 'overview'],
    queryFn: () => api.get<AdminOverview>('/api/admin/overview'),
  });

  const { data, isPending, error, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ['admin', 'activity', tenantId, action],
      initialPageParam: null as number | null,
      queryFn: ({ pageParam }) => {
        const qs = new URLSearchParams({ limit: String(PAGE_SIZE) });
        if (tenantId) qs.set('tenantId', tenantId);
        if (action) qs.set('action', action);
        if (pageParam) qs.set('before', String(pageParam));
        return api.get<ActivityPage>(`/api/admin/activity?${qs}`);
      },
      getNextPageParam: (last) => last.nextBefore,
    });

  function setFilter(key: string, value: string) {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  }

  const events = data?.pages.flatMap((p) => p.events) ?? [];

  return (
    <div>
      <header className="admin-head">
        <p className="eyebrow">Milepost Safety Group · platform console</p>
        <h1 className="admin-title">Activity</h1>
        <p className="muted" style={{ maxWidth: '64ch' }}>
          Every audited action across every carrier, newest first. Driver identities are
          masked here — the audit trail inside a carrier's own workspace names them.
        </p>
      </header>

      <div className="activity-filters">
        <div className="field">
          <label htmlFor="activity-tenant">Carrier</label>
          <select
            id="activity-tenant"
            value={tenantId}
            onChange={(e) => setFilter('tenantId', e.target.value)}
          >
            <option value="">All carriers</option>
            {overview?.tenants.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label htmlFor="activity-action">Action</label>
          <select
            id="activity-action"
            value={action}
            onChange={(e) => setFilter('action', e.target.value)}
          >
            {ACTION_FILTERS.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {isPending && <p className="muted">Loading…</p>}
      {error && <p className="field-error">{(error as Error).message}</p>}

      {!isPending && !error && (
        <>
          <ActivityTable events={events} showTenant={!tenantId} />

          <div style={{ marginTop: 'var(--sp-4)' }}>
            {hasNextPage ? (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => void fetchNextPage()}
                disabled={isFetchingNextPage}
              >
                {isFetchingNextPage ? 'Loading…' : 'Load older'}
              </button>
            ) : (
              events.length > 0 && <p className="cite">That is the whole trail for this filter.</p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
